import React, {Fragment, useEffect, useReducer, useState} from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    Typography, useMediaQuery, useTheme
} from "@mui/material";
import Button from "@mui/material/Button";
import {Lesson} from "../../interfaces/lesson";
import RecordResultsDialog from "../dialogs/recordResultsDialog";
import styles from './trainingChallenges.module.css';
import {axiosRequest} from "../../helpers/axios";
import {DrillRecord} from "../../interfaces/drillRecord";
import dayjs from "dayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PGEditIcon from "../icons/PGEdit";
import PGDeleteIcon from "../icons/PGDelete";
import PGEditIconOrange from "../icons/PGEditOrange";
import PGDeleteIconOrange from "../icons/PGDeleteOrange";
import {
    DrillAccordion,
    DrillAccordionDetails,
    DrillAccordionSummary, DrillTableRow, DrillTableCell,
    DrillTableHeaderCell
} from "./trainingChallangesTableAndAccordion";
import parse from 'html-react-parser';
import { isDrillsPath } from '../../helpers/utils';
import { useLocation, useNavigate } from 'react-router';
import VideoDialog from '../dialogs/videoDialog/videoDialog';

function reducer(lessons: Lesson[], action: { type: string, payload: any }) {
    switch (action.type) {
        case 'set' : {
            return action.payload
        }
        case 'addLessonRecord' : {
            lessons.find(el => el.ID === action.payload.lessonID)?.records.push(action.payload.record)
            return lessons;
        }
        case 'removeLessonRecord' : {
            return lessons.map(el => {
                return {...el, records: el.records.filter(record => record.record_id !== action.payload.record_id)}
            })
        }
        case 'editLessonRecord' : {
            return lessons.map(el => {
                return {
                    ...el,
                    records: el.records.map(record => record.record_id === action.payload.record.record_id ? action.payload.record : record)
                }
            })
        }
        default : {
            return lessons;
        }
    }
}


const averageValue = (records: DrillRecord[]) => records.length ? (records.reduce((total: number, next: DrillRecord) => total + next.score, 0) / records.length).toFixed(2) : "-- --";

const TrainingChallenges = ({drillLessons, passedLesson,currentLesson,editRecordDrill,removeDrillRecord}: { drillLessons: Lesson[], passedLesson?:any,currentLesson?: Lesson ,removeDrillRecord?: (lessonID: number, recordID: number) => void,editRecordDrill?: (lessonID: number, updatedRecord: DrillRecord) => void}) => {
    const [recordResultDialogOpen, setRecordResultDialogOpen] = useState(false);
    const [currentLessonID, setCurrentLessonID] = useState(0);
    const [showVideo, setShowVideo] = useState(false);
    const [drills, dispatch] = useReducer(reducer, []);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentRecordID, setCurrentRecordID] = useState(0);
    const [expanded, setExpanded] = useState<number | false>(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        dispatch({type: 'set', payload: drillLessons})
    }, [drillLessons]);

    const editRecord = (record: DrillRecord) => {
        setCurrentRecordID(record.record_id);
        setShowEditModal(true);
    }

    const deleteRecord = (record_id: number, lessonID: number) => {
        axiosRequest("DELETE", "/wp-json/pg/v2/sc/score-tracker/" + record_id)
            .then(response => {
                dispatch({type: "removeLessonRecord", payload: {lessonID: lessonID, record_id: record_id}})
                if(removeDrillRecord){
                    removeDrillRecord(lessonID,record_id)
                }
            })
    }

    const addNewRecordToDill = (data: any, lessonID: number) => {
        dispatch({
            type: 'addLessonRecord', payload: {
                lessonID: lessonID,
                record: {
                    record_id: data.record_id,
                    record_date: data.record_date,
                    score: data.score
                }
            }
        })
    }

    const editDrillRecord = (data: any, lessonID: number) => {
        dispatch({
            type: 'editLessonRecord', payload: {
                lessonID: lessonID,
                record: {
                    record_id: data.record_id,
                    record_date: data.record_date,
                    score: data.score
                }
            }
        })
    }

    const handleChange =
        (panel: number) => (event: React.SyntheticEvent, newExpanded: boolean) => {
            setExpanded(newExpanded ? panel : false);
    };

    let lessonIdToScroll= 0;
        if (passedLesson) {
             lessonIdToScroll = passedLesson.id.ID;
        }

        useEffect(() => {
            // Scroll to the lesson with a specific ID after the component mounts
            const scrollToLesson = () => {
                const element = document.getElementById(`lesson-${lessonIdToScroll}`);
                if (element) {
                    element.scrollIntoView({
                        behavior: 'auto',
                        block: 'start',
                    });
                } else {
                    console.error(`Lesson with ID "${lessonIdToScroll}" not found.`);
                }
            };

            // if (!mobilePhone) {
                const timeoutId = setTimeout(scrollToLesson, 1500);
                return () => clearTimeout(timeoutId);
            // }

        }, [lessonIdToScroll, passedLesson]);

    const checkScAccess = () => {
        if (typeof window !== 'undefined' && window.pgUser?.products) {
            return window.pgUser.products.some((product: string) => product === '1' || product === 'Scratch Club'); // Scratch Club product ID
        }
        return false;
    };

    return (
        <Box 
        sx={
            isDrillsPath(location.pathname)
              ? { marginTop: "40px" }
              : {
                  borderRadius: "10px",
                  padding: { xs: 0, md: "0" },
                  marginTop: "40px",
            marginBottom:{xs:0,md:'40px'},
                }
          }
          id={"pg-training-challenges"}
        >
            <Typography sx={{fontSize: isMobile ? 25 : 22, fontWeight: isMobile ? 600 :500, marginBottom: {xs: "20px", md: 0}, fontFamily: isMobile ? "'Lato', sans-serif" : "'Repro', sans-serif", textTransform: isMobile ? "capitalize" : "uppercase"}}>{isDrillsPath(location.pathname) ? "Drill Scores" : "Training Challenges"}</Typography>
        {
            drills.map((lesson: Lesson) => {
                const regex = /video\/(\d+)/;
                const match = lesson.video.embed_url.match(regex);
                const videoID = match? match[1] : 1;
                return (<>
                    <Box id={`lesson-${lesson.ID}`} key={lesson.ID}></Box>
                    <Fragment key={lesson.ID}>
                        {isMobile &&
                        <DrillAccordion sx={{marginBottom: "15px", border: "2px solid #F4F2F0"}} expanded={expanded === lesson.ID || isDrillsPath(location.pathname)}
                                                onChange={handleChange(lesson.ID)}>
                            {!isDrillsPath(location.pathname) && <DrillAccordionSummary aria-controls="panel1d-content" id="panel1d-header"
                                              expandIcon={<ExpandMoreIcon/>}>
                                <Typography sx={{
                                    fontSize: 15,
                                    fontWeight: 800,
                                    margin: "auto 0",
                                    fontFamily: isMobile ? "'Lato', sans-serif" : "'Repro', sans-serif"
                                }}>{lesson.name}</Typography>
                                {expanded !== lesson.ID &&
                                <Box sx={{
                                    minWidth: "106px",
                                    margin: "auto 0",
                                    backgroundColor: "#EFF2FD",
                                    padding: "8px",
                                    display: "flex",
                                    justifyContent:"space-between",
                                    fontFamily: isMobile ? "'Lato', sans-serif" : "'Repro', sans-serif"
                                }}>
                                   <Typography  sx={{
                                       fontSize: "12px",
                                       lineHeight: "18px",
                                       fontWeight: 400,
                                       fontFamily: isMobile ? "'Lato', sans-serif" : "'Repro', sans-serif"
                                   }}>Total Avg</Typography>
                                    <Typography sx={{
                                        fontSize: "12px",
                                        lineHeight: "18px",
                                        fontWeight: 400,
                                        marginLeft: "10px",
                                        fontFamily: isMobile ? "'Lato', sans-serif" : "'Repro', sans-serif"
                                    }}>{averageValue(lesson.records) ? averageValue(lesson.records) : "-- --"}</Typography>
                                </Box>
                                }
                            </DrillAccordionSummary>}
                            <DrillAccordionDetails sx={{padding:`${isDrillsPath(location.pathname) && 0}`,border: "none"}}>
                                <TableContainer component={Paper} sx={{boxShadow: "none"}}>
                                    <Table sx={{minWidth: 300}} aria-label="customized table">
                                        <TableHead>
                                            <TableRow>
                                                <DrillTableHeaderCell sx={{ color: '#7B726C' }}>Date</DrillTableHeaderCell>
                                                <DrillTableHeaderCell sx={{ color: '#7B726C' }}>Number of Shots</DrillTableHeaderCell>
                                            </TableRow>
                                            <TableRow sx={{backgroundColor: "#EFF2FD"}}>
                                                <DrillTableHeaderCell sx={{width: "30%", color: '#7B726C'}}>Total Average</DrillTableHeaderCell>
                                                <DrillTableHeaderCell
                                                    sx={{width: "70%", color: '#7B726C'}}>{averageValue(lesson.records)}</DrillTableHeaderCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {lesson.records.map((record: DrillRecord) => (
                                                <Fragment key={record.record_id}>
                                                    <DrillTableRow>
                                                        <DrillTableCell sx={{width: "30%", color: '#7B726C'}}>
                                                            {dayjs(record.record_date).format("MM/DD/YYYY")}
                                                        </DrillTableCell>
                                                        <DrillTableCell sx={{width: "70%", color: '#7B726C'}}>
                                                            <Box sx={{display: "flex"}}>
                                                                <Typography
                                                                    sx={{
                                                                        margin: "auto 0",
                                                                        fontSize: 12
                                                                    }}>{record.score}</Typography>
                                                                <Box sx={{
                                                                    marginLeft: "auto",
                                                                    display: "flex",
                                                                    justifyContent: "flex-end"
                                                                }}>
                                                                    <Button variant="text"
                                                                            onClick={() => editRecord(record)}
                                                                            className={styles.tableActions}
                                                                            disableRipple startIcon={<PGEditIconOrange/>}
                                                                            sx={{color: '#FD3300 !important', 
                                                                                fontSize: '10px !important',
                                                                                fontWeight: '600 !important',
                                                                            }}>
                                                                        Edit
                                                                    </Button>
                                                                    <Button variant="text"
                                                                            onClick={() => deleteRecord(record.record_id, lesson.ID)}
                                                                            className={styles.tableActions}
                                                                            disableRipple
                                                                            startIcon={<PGDeleteIconOrange/>}
                                                                            sx={{color: '#FD3300 !important', 
                                                                                fontSize: '10px !important',
                                                                                fontWeight: '600 !important',
                                                                            }}>
                                                                        Delete
                                                                    </Button>
                                                                </Box>
                                                            </Box>
                                                        </DrillTableCell>
                                                    </DrillTableRow>
                                                    <RecordResultsDialog
                                                        show={currentRecordID === record.record_id && showEditModal}
                                                        setShow={(value) => setShowEditModal(value)}
                                                        lesson={lesson}
                                                        record={record}
                                                        callback={(data) => editDrillRecord(data, lesson.ID)}
                                                        routeComing={"record"}
                                                    />
                                                </Fragment>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                               {!isDrillsPath(location.pathname) && <Box sx={{display: "flex", justifyContent: "space-between", marginTop: "15px"}}>
                                    {!lesson.content && <Button
                                        className={styles.button}
                                        disableRipple
                                        onClick={() => {
                                            if(checkScAccess()) {
                                                setShowVideo(true)
                                                setCurrentLessonID(lesson.ID)
                                            } else {
                                                window.location.href = '/scratch-club-benefits/';
                                            }
                                        }}
                                        sx={{
                                            fontSize: "16px", 
                                            margin: 0, 
                                            minWidth: "154px", 
                                            minHeight: "41px",
                                            '&:hover': {
                                                backgroundColor: '#F4F2F0'
                                            }
                                        }}
                                        variant="outlined">Watch video</Button>
                                    }
                                    <Button
                                        className={styles.button}
                                        style={{marginRight: 0}}
                                        disableRipple
                                        onClick={() => {
                                            setRecordResultDialogOpen(true)
                                            setCurrentLessonID(lesson.ID)
                                        }}
                                        sx={{
                                            fontSize: "16px", 
                                            margin: 0, 
                                            minWidth: "154px", 
                                            minHeight: "41px",
                                            '&:hover': {
                                                backgroundColor: '#F4F2F0'
                                            }
                                        }}
                                        variant="outlined">Record result</Button>
                                </Box>}
                                {isDrillsPath(location.pathname) && <Typography onClick={()=>{
                                    navigate("/scratch-club/record")
                                }} fontSize={16} fontWeight={700} color={'#4362ED'} sx={{textDecoration:'underline',cursor:'pointer',textUnderlineOffset:'4px',marginLeft:'4px'}} variant='subtitle1'>View All Drill Scores</Typography>}
                            </DrillAccordionDetails>
                        </DrillAccordion>}

                        {!isMobile && <>
                        {/* Dzelal */}
                            {!isDrillsPath(location.pathname) && <Box sx={{display: "flex", margin: "30px 0 10px 0"}}>
                                <Typography
                                    sx={{
                                        margin: "auto 0",
                                        fontFamily: isMobile ? "'Lato', sans-serif" : "'Repro', sans-serif",
                                        fontWeight: isMobile ? 800 : 400,
                                        fontSize: isMobile ? 15 : 16,
                                    }}>{lesson.name}</Typography>
                                <Box sx={{marginLeft: "auto"}}>
                                    {!lesson.content &&<Button
                                        className={styles.button}
                                        disableRipple
                                        onClick={() => {
                                            if(checkScAccess()) {
                                                setShowVideo(true)
                                                setCurrentLessonID(lesson.ID)
                                            } else {
                                                window.location.href = '/scratch-club-benefits/';
                                            }
                                        }}
                                        variant="outlined"
                                        sx={{
                                            fontSize: '16px !important',
                                            padding: '5px 25px !important',
                                            '&:hover': {
                                                backgroundColor: '#F4F2F0'
                                            }
                                        }}>Watch video</Button>
                                    }
                                    <Button
                                        className={styles.button}
                                        disableRipple
                                        onClick={() => {
                                            setRecordResultDialogOpen(true)
                                            setCurrentLessonID(lesson.ID)
                                        }}
                                        variant="outlined"
                                        sx={{
                                            fontSize: '16px !important',
                                            padding: '5px 19px !important',
                                            '&:hover': {
                                                backgroundColor: '#F4F2F0'
                                            }
                                        }}>Record result</Button>
                                </Box>
                            </Box>}
                            <TableContainer component={Paper} sx={{boxShadow: "none"}}>
                                <Table sx={{minWidth: 700}} aria-label="customized table">
                                    <TableHead>
                                        <TableRow>
                                            <DrillTableHeaderCell>Date</DrillTableHeaderCell>
                                            <DrillTableHeaderCell sx={{display:'flex', justifyContent:'space-between'}}>
                                                {/*<>
                                                <>{parse(lesson.drill.subheading)}</>
                                                {isDrillsPath(location.pathname) && <Typography
                                                onClick={()=>{
                                                    navigate("/scratch-club/record")
                                                }}
                                                fontWeight={700} color={'#4362ED'} sx={{textDecoration:'underline',cursor:'pointer',textUnderlineOffset:'4px',marginLeft:'4px',fontSize:'16px'}} variant='subtitle1'>View All Drill Scores</Typography>}
                                                </>*/}
                                                Number of Shots
                                                </DrillTableHeaderCell>
                                        </TableRow>
                                        <TableRow sx={{backgroundColor: "#F4F2F0"}}>
                                            <DrillTableHeaderCell sx={{width: "20%"}}>Total Average</DrillTableHeaderCell>
                                            <DrillTableHeaderCell
                                                sx={{width: "80%"}}>{averageValue(lesson.records)}</DrillTableHeaderCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {lesson.records.map((record: DrillRecord) => (
                                            <Fragment key={record.record_id}>
                                                <DrillTableRow>
                                                    <DrillTableCell sx={{width: "20%"}}>
                                                        {dayjs(record.record_date).format("MM/DD/YYYY")}
                                                    </DrillTableCell>
                                                    <DrillTableCell sx={{width: "80%"}}>
                                                        <Box sx={{display: "flex"}}>
                                                            <Typography
                                                                sx={{
                                                                    margin: "auto 0",
                                                                    fontSize: 12
                                                                }}>{record.score}</Typography>
                                                            <Box sx={{marginLeft: "auto"}}>
                                                                <Button variant="text"
                                                                        onClick={() => editRecord(record)}
                                                                        className={styles.tableActionsMD}
                                                                        disableRipple startIcon={<PGEditIcon/>}
                                                                        sx={{
                                                                            color: '#1D1A1A !important',
                                                                            fontSize: '10px !important',
                                                                            fontWeight: "600 !important",
                                                                        }}>
                                                                    Edit
                                                                </Button>
                                                                <Button variant="text"
                                                                        onClick={() => deleteRecord(record.record_id, lesson.ID)}
                                                                        className={styles.tableActionsMD}
                                                                        disableRipple startIcon={<PGDeleteIcon/>}
                                                                        sx={{
                                                                            color: '#1D1A1A !important',
                                                                            fontSize: '10px !important',
                                                                            fontWeight: "600 !important",
                                                                        }}>
                                                                    Delete
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    </DrillTableCell>
                                                </DrillTableRow>
                                                <RecordResultsDialog
                                                    show={currentRecordID === record.record_id && showEditModal}
                                                    setShow={(value) => setShowEditModal(value)}
                                                    lesson={lesson}
                                                    record={record}
                                                    callback={(data) => editDrillRecord(data, lesson.ID)}
                                                    routeComing={"record"}
                                                />
                                            </Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                        }
                        <RecordResultsDialog
                            show={lesson.ID === currentLessonID && recordResultDialogOpen}
                            setShow={(value) => setRecordResultDialogOpen(value)}
                            lesson={lesson}
                            callback={(data) => addNewRecordToDill(data, lesson.ID)}
                            routeComing={"record"}
                        />
                        <VideoDialog
                            show={lesson.ID === currentLessonID && showVideo}
                            setShow={(value) => setShowVideo(value)}
                            video={lesson.video.embed_url}
                            videoID={videoID}
                            title={lesson.name}
                        />
                    </Fragment></>
                )
            }
        )}
        </Box>
    );
};


export default TrainingChallenges;